import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  DatePicker,
  message,
  Tag,
  Tooltip,
  Row,
  Col,
  Statistic,
  Progress,
  Avatar,
  Space,
  Skeleton,
  Upload,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  HomeOutlined,
  DollarOutlined,
  UserOutlined,
  CalendarOutlined,
  ToolOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  ExclamationCircleOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import { AuthContext } from "../context/AuthProvider";
import unitModel from "../lib/firebase/unitModel";
import { listTenants, updateTenantRent } from "../lib/firebase/tenantModel";
import PageHeader from "../components/PageHeader";
import { StatCard, EmptyState, CardHeader } from "../components/shared";
import { propertyKeys } from "../lib/queryKeys";
import { trackEvent } from "../helpers/mixpanel";
import { discordTrack } from "../lib/analytics/discordTrack";
import { uploadImage } from "../helpers/firebase";
import {
  trackPageView,
  trackFeatureUsage,
  trackModalInteraction,
  trackFormSubmission,
  trackDataAction,
  trackButtonClick,
  trackTimeOnPage,
} from "../helpers/dashboardAnalytics";
import UpgradeModal from "../components/UpgradeModal";
import {
  canAddUnit,
  getEffectiveUnitCap,
  getNextTierUpgradeName,
  getPricingSchedule,
  isFreeTierUser,
  getRawSubscriptionPlanKey,
  normalizePlanKeyForSchedule,
} from "../lib/pricing";
import { LAYOUT_CONSTANTS, useBreakpoints } from "../lib/breakpoints";
import { getDebugMode, getDebugUnitCount } from "../helpers/constants";
import { getDebugUnitsExtended, getDebugTenants } from "../helpers/debugData";
import { ensureWashingtonHighCompliance } from "../helpers/compliance";
import { getUnitDisplayAddress } from "../lib/unitAddressUtils";
import { isTenantActive } from "../lib/leaseUtils";

const { Option } = Select;
const { TextArea } = Input;

/** Parse legacy "123 Main St, City, ST 12345" or "123 Main St, City, ST" into discrete parts. */
function parseLegacyAddress(addr) {
  if (!addr || typeof addr !== "string")
    return { street: "", city: "", state: "", zip: "" };
  const parts = addr
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length >= 4) {
    const zip = (parts[3] || "").replace(/\D/g, "").slice(0, 5);
    return {
      street: parts[0] || "",
      city: parts[1] || "",
      state: (parts[2] || "").slice(0, 2).toUpperCase(),
      zip,
    };
  }
  if (parts.length === 3) {
    const last = parts[2] || "";
    const stateMatch = last.match(/\b([A-Za-z]{2})\b/);
    const zipMatch = last.match(/\b(\d{5})(?:-\d{4})?\b/);
    return {
      street: parts[0] || "",
      city: parts[1] || "",
      state: stateMatch
        ? stateMatch[1].toUpperCase()
        : last.slice(0, 2).toUpperCase(),
      zip: zipMatch ? zipMatch[1] : "",
    };
  }
  if (parts.length === 2)
    return { street: parts[0] || "", city: parts[1] || "", state: "", zip: "" };
  return { street: parts[0] || "", city: "", state: "", zip: "" };
}

const StyledModal = styled(Modal)`
  --lo-modal-header-height: 76px;
  --lo-modal-close-size: 36px;

  .ant-modal-content {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    padding: 0;
    max-height: calc(100dvh - 32px);
    display: flex;
    flex-direction: column;
  }

  .ant-modal-header {
    background: linear-gradient(
      135deg,
      var(--color-primary-darker) 0%,
      var(--color-primary-dark) 100%
    );
    border-bottom: none;
    border-radius: 12px 12px 0 0;
    padding: 1.5rem 2rem;
    margin: 0;
    min-height: var(--lo-modal-header-height);
    box-sizing: border-box;
    display: flex;
    align-items: center;
  }

  .ant-modal-title {
    color: white !important;
    font-family: var(--font-primary);
    font-weight: 700;
    font-size: 1.5rem;
    line-height: 1.2;
    letter-spacing: -0.02em;
    text-align: center;
  }

  .ant-modal-close {
    color: white !important;
    font-size: 18px;
    top: calc(
      (var(--lo-modal-header-height) - var(--lo-modal-close-size)) / 2
    ) !important;
    right: 20px;
    background: transparent !important;
    border-radius: 0 !important;
    width: var(--lo-modal-close-size) !important;
    height: var(--lo-modal-close-size) !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    border: none !important;
    transition: color 0.2s ease !important;

    &:hover {
      color: white !important;
      background: transparent !important;
      border-color: transparent !important;
    }
  }

  .ant-modal-close .ant-modal-close-x,
  .ant-modal-close .ant-modal-close-icon,
  .ant-modal-close .anticon,
  .ant-modal-close svg,
  .ant-modal-close:hover .ant-modal-close-x,
  .ant-modal-close:hover .ant-modal-close-icon,
  .ant-modal-close:hover .anticon,
  .ant-modal-close:hover svg {
    color: #ffffff !important;
    fill: #ffffff !important;
  }

  .ant-modal-body {
    padding: 2rem;
    background: var(--color-bg-secondary);
    overflow-x: hidden;
    overflow-y: auto;
    min-height: 0;
  }

  /* Uniform input/select heights (40px to match taller controls) and proper dropdown arrow centering in Add/Edit Unit modal */
  .ant-form-item .ant-input:not(textarea),
  .ant-form-item .ant-input-number,
  .ant-form-item .ant-input-affix-wrapper,
  .ant-form-item .ant-select-selector {
    height: 40px !important;
    min-height: 40px !important;
  }

  .ant-form-item .ant-input-number .ant-input-number-input {
    height: 38px !important;
    min-height: 38px !important;
  }

  /* Keep all numeric form values and currency prefixes visually consistent. */
  .ant-form-item input[type="number"],
  .ant-form-item .ant-input-number-input,
  .ant-form-item .ant-input-prefix {
    font-family: var(--font-secondary) !important;
    font-size: 0.9rem !important;
    font-weight: 400 !important;
  }

  /* Affix wrapper: flex alignment so prefix ($) and input align vertically */
  .ant-form-item .ant-input-affix-wrapper {
    display: flex !important;
    align-items: center !important;
  }

  .ant-form-item .ant-input-affix-wrapper .ant-input {
    flex: 1 !important;
    min-height: 0 !important;
    height: 100% !important;
    padding: 0 11px !important;
    line-height: 1.5 !important;
    border: none !important;
  }

  .ant-form-item .ant-input-affix-wrapper .ant-input-prefix {
    display: flex !important;
    align-items: center !important;
  }

  /* Textareas - ensure adequate height, not forced to single-line size */
  .ant-form-item textarea.ant-input {
    min-height: 80px !important;
    height: auto !important;
  }

  .ant-form-item .ant-select {
    height: 40px !important;
  }

  .ant-form-item .ant-select .ant-select-selector {
    height: 40px !important;
    min-height: 40px !important;
    display: flex !important;
    align-items: center !important;
    padding-right: 30px !important;
  }

  .ant-form-item .ant-select .ant-select-selection-item,
  .ant-form-item .ant-select .ant-select-selection-placeholder {
    line-height: 1.5 !important;
    display: flex !important;
    align-items: center !important;
  }

  .ant-form-item .ant-select .ant-select-arrow {
    position: absolute !important;
    right: 11px !important;
    top: 50% !important;
    transform: translateY(-50%) !important;
    margin: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }
`;

const Units = () => {
  const { user, userInfo, isOpsChatOnlyUser } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const { isMobile } = useBreakpoints();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isUpgradeModalVisible, setIsUpgradeModalVisible] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [unitPhotoUrl, setUnitPhotoUrl] = useState(null);
  const [unitPhotoFileList, setUnitPhotoFileList] = useState([]);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [form] = Form.useForm();
  const [pageSize, setPageSize] = useState(10);

  // Debug mode - automatically determined by route
  const isDebugMode = getDebugMode();
  const debugUnitCount = getDebugUnitCount();

  // Load data using useQuery for better caching and performance
  const { data: units = [], isLoading: isUnitsLoading } = useQuery({
    queryKey: propertyKeys.units(user?.uid, isDebugMode, debugUnitCount),
    queryFn: () => {
      if (isDebugMode) {
        return Promise.resolve(getDebugUnitsExtended(debugUnitCount));
      }
      return unitModel.getByOwner(user.uid);
    },
    enabled: !!user?.uid || isDebugMode,
  });

  const { data: tenantsData, isLoading: isTenantsLoading } = useQuery({
    queryKey: propertyKeys.tenants(user?.uid, isDebugMode, debugUnitCount),
    queryFn: () => {
      if (isDebugMode) {
        // Pass unitCount so tenants match the occupied units (85% occupancy)
        return Promise.resolve({ tenants: getDebugTenants(debugUnitCount) });
      }
      return listTenants();
    },
    enabled: !!user?.uid || isDebugMode,
  });

  const tenants = tenantsData?.tenants || [];

  // Derive subscription tier (same precedence as backend / door caps)
  const subscriptionTier = getRawSubscriptionPlanKey(userInfo) || "starter";
  const pricingTierGroup = userInfo?.pricingTierGroup || "market_2026";
  const tierKeyRaw = String(subscriptionTier).toLowerCase();
  const normalizedTier = normalizePlanKeyForSchedule(
    tierKeyRaw,
    pricingTierGroup
  );

  // Check if user can add more units based on their subscription and pricing schedule
  const canAddMoreUnits = canAddUnit(
    normalizedTier,
    units.length,
    pricingTierGroup,
    userInfo
  );
  const unitCap = getEffectiveUnitCap(userInfo, pricingTierGroup);
  const nextTierName = getNextTierUpgradeName(normalizedTier, pricingTierGroup);
  const addDoorCapTooltip = !canAddMoreUnits
    ? nextTierName
      ? `Cap reached: Upgrade to ${nextTierName} to add more doors.`
      : "Cap reached: contact us for custom portfolio capacity."
    : undefined;
  const navigate = useNavigate();
  /** PLG wedge: free users see a "Summon Leo" CTA on the zero-unit empty state. */
  const isFreeUser = isFreeTierUser(userInfo);
  const handleSummonLeo = () => {
    trackEvent("summon_leo_clicked", {
      source: "units_empty_state",
      user_id: user?.uid,
    });
    navigate("/leo?context=first-unit");
  };

  // Track page view and time on page
  useEffect(() => {
    trackPageView("units", {
      user_id: user?.uid,
      user_email: user?.email,
      units_count: units.length,
      tenants_count: tenants.length,
      subscription_tier: subscriptionTier,
    });

    // Track time on page
    const startTime = Date.now();
    return () => {
      const timeSpent = Math.round((Date.now() - startTime) / 1000);
      trackTimeOnPage("units", timeSpent);
    };
  }, [user?.uid, user?.email, units.length, tenants.length, subscriptionTier]);
  const loading = isUnitsLoading || isTenantsLoading;

  const showModal = (unit = null) => {
    if (!unit && !canAddMoreUnits) {
      setIsUpgradeModalVisible(true);
      return;
    }
    trackModalInteraction("unit", "opened", {
      user_id: user?.uid,
      action: unit ? "edit" : "add",
      unit_id: unit?.id,
      units_count: units.length,
    });
    setEditingUnit(unit);
    setIsModalVisible(true);
    const existingPhoto =
      Array.isArray(unit?.photos) && unit.photos.length > 0
        ? unit.photos[0]
        : null;
    setUnitPhotoUrl(existingPhoto);
    setUnitPhotoFileList(
      existingPhoto
        ? [
            {
              uid: "-1",
              name: "door-photo.jpg",
              status: "done",
              url: existingPhoto,
            },
          ]
        : []
    );
    try {
      trackEvent(unit ? "unit_edit_open" : "unit_create_open", {
        unitId: unit?.id,
      });
    } catch (err) {
      console.warn("[Units] Failed to track unit open modal:", err);
    }
    if (unit) {
      const hasDiscrete =
        unit.address_street ||
        unit.address_city ||
        unit.address_state ||
        unit.address_zip;
      const parsed = hasDiscrete
        ? {
            address_street: unit.address_street || "",
            address_city: unit.address_city || "",
            address_state: unit.address_state || "",
            address_zip: unit.address_zip || "",
          }
        : (() => {
            const p = parseLegacyAddress(unit.address);
            return {
              address_street: p.street,
              address_city: p.city,
              address_state: p.state,
              address_zip: p.zip,
            };
          })();
      form.setFieldsValue({
        ...unit,
        ...parsed,
        mortgage: unit.mortgage
          ? {
              ...unit.mortgage,
              monthlyPayment: unit.mortgage.monthlyPayment ?? 0,
            }
          : { principal: 0, monthlyPayment: 0, interestRate: 0, term: 30 },
      });
    } else {
      form.resetFields();
      setUnitPhotoUrl(null);
      setUnitPhotoFileList([]);
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      setModalLoading(true);

      let rentChanged = false;
      let unitTenants = [];

      if (editingUnit) {
        // Check if rent is being updated and if there's a tenant attached
        rentChanged =
          !isOpsChatOnlyUser && editingUnit.monthlyRent !== values.monthlyRent;
        unitTenants = getTenantsForUnit(editingUnit.id);

        if (rentChanged && unitTenants.length > 0) {
          // Show confirmation modal for updating tenant rent
          const tenantNames = unitTenants.map((t) => t.name).join(", ");
          const confirmUpdate = await new Promise((resolve) => {
            Modal.confirm({
              title: "Update Tenant Rent?",
              content: (
                <div>
                  <p>
                    The rent for this door has changed from{" "}
                    <strong>
                      ${editingUnit.monthlyRent?.toLocaleString() || "0"}
                    </strong>{" "}
                    to{" "}
                    <strong>
                      ${values.monthlyRent?.toLocaleString() || "0"}
                    </strong>
                    .
                  </p>
                  <p>
                    Would you like to also update the rent for the tenant(s) in
                    this door?
                  </p>
                  <p>
                    <strong>Tenant(s):</strong> {tenantNames}
                  </p>
                </div>
              ),
              okText: "Yes, Update Tenant Rent",
              cancelText: "No, Keep Current Tenant Rent",
              onOk: () => resolve(true),
              onCancel: () => resolve(false),
            });
          });

          if (confirmUpdate) {
            // Update tenant rent for all tenants in this unit
            for (const tenant of unitTenants) {
              try {
                await updateTenantRent(
                  tenant.id,
                  values.monthlyRent,
                  "unit_rent_update"
                );
                message.success(`Updated rent for ${tenant.name}`);
              } catch (error) {
                console.error(
                  `Failed to update rent for tenant ${tenant.name}:`,
                  error
                );
                message.error(`Failed to update rent for ${tenant.name}`);
              }
            }
          }
        }

        // Update existing unit in Firebase (preserve existing mortgage fields, update monthlyPayment)
        const { mortgage: formMortgage, ...restValues } = values;
        const fullAddress = [
          restValues.address_street,
          restValues.address_city,
          restValues.address_state,
          restValues.address_zip,
        ]
          .filter(Boolean)
          .join(", ");
        await unitModel.update(editingUnit.id, {
          ...restValues,
          address: fullAddress,
          notes: values.notes || "",
          photos: unitPhotoUrl ? [unitPhotoUrl] : [],
          mortgage: {
            ...(editingUnit.mortgage || {}),
            principal: editingUnit.mortgage?.principal ?? 0,
            monthlyPayment: Number(formMortgage?.monthlyPayment) || 0,
            interestRate: editingUnit.mortgage?.interestRate ?? 0,
            term: editingUnit.mortgage?.term ?? 30,
          },
        });
        message.success("Door updated successfully!");
        try {
          trackEvent("unit_update_success", { unitId: editingUnit.id });
        } catch (err) {
          console.warn("[Units] Failed to track unit_update_success:", err);
        }
        await ensureWashingtonHighCompliance(
          user?.uid,
          fullAddress,
          restValues.address_state,
          {
            invalidateSettings: (uid) =>
              queryClient.invalidateQueries({
                queryKey: propertyKeys.settings(uid),
              }),
          }
        );
      } else {
        // Enforce unit cap before creating (guard against race / stale UI)
        if (
          !canAddUnit(normalizedTier, units.length, pricingTierGroup, userInfo)
        ) {
          setIsUpgradeModalVisible(true);
          setModalLoading(false);
          return;
        }
        // Create new unit in Firebase
        const { mortgage: formMortgage, ...restValues } = values;
        const fullAddress = [
          restValues.address_street,
          restValues.address_city,
          restValues.address_state,
          restValues.address_zip,
        ]
          .filter(Boolean)
          .join(", ");
        const newUnit = await unitModel.create({
          ...restValues,
          address: fullAddress,
          notes: values.notes || "",
          status: "Active",
          occupied: 0,
          photos: unitPhotoUrl ? [unitPhotoUrl] : [],
          mortgage: {
            principal: 0,
            monthlyPayment: Number(formMortgage?.monthlyPayment) || 0,
            interestRate: 0,
            term: 30,
          },
        });
        message.success(`${newUnit.name} door added successfully!`);
        try {
          trackEvent("unit_create_success", { unitId: newUnit.id });
        } catch (err) {
          console.warn("[Units] Failed to track unit_create_success:", err);
        }
        discordTrack("unit_added", {
          address: fullAddress,
          unitName: newUnit.name,
        });
        await ensureWashingtonHighCompliance(
          user?.uid,
          fullAddress,
          restValues.address_state,
          {
            invalidateSettings: (uid) =>
              queryClient.invalidateQueries({
                queryKey: propertyKeys.settings(uid),
              }),
          }
        );
      }

      // Invalidate and refetch units data
      await queryClient.invalidateQueries({
        queryKey: propertyKeys.units(user?.uid),
      });

      // Also invalidate tenants data if we updated tenant rent
      if (rentChanged && unitTenants.length > 0) {
        await queryClient.invalidateQueries({
          queryKey: propertyKeys.tenants(user?.uid),
        });
      }

      setIsModalVisible(false);
      setEditingUnit(null);
      setUnitPhotoUrl(null);
      setUnitPhotoFileList([]);
      form.resetFields();
    } catch (error) {
      console.error("Error saving unit:", error);
      message.error("Failed to save door. Please try again.");
      try {
        trackEvent("unit_save_error", {
          message: String(error),
          unitId: editingUnit?.id,
        });
      } catch (err) {
        console.warn("[Units] Failed to track unit_save_error:", err);
      }
    } finally {
      setModalLoading(false);
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setEditingUnit(null);
    setUnitPhotoUrl(null);
    setUnitPhotoFileList([]);
    form.resetFields();
  };

  const handlePageSizeChange = (current, size) => {
    setPageSize(size);
  };

  const handleDeleteUnit = (unitId) => {
    Modal.confirm({
      title: null,
      icon: null,
      centered: true,
      content: (
        <div
          style={{
            fontFamily: "var(--font-secondary)",
            color: "var(--color-text)",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontSize: "3rem",
              marginBottom: "1rem",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <ExclamationCircleOutlined
              aria-label="Warning"
              style={{ color: "#000000" }}
            />
          </div>
          <h3
            style={{
              fontFamily: "var(--font-primary)",
              fontSize: "1.125rem",
              fontWeight: 700,
              color: "var(--color-text)",
              margin: "0 0 0.75rem 0",
              lineHeight: "1.4",
            }}
          >
            Delete this door?
          </h3>
          <p
            style={{
              fontSize: "0.875rem",
              color: "var(--color-text-light)",
              margin: 0,
              lineHeight: "1.5",
            }}
          >
            This action cannot be undone.
          </p>
        </div>
      ),
      okText: "Delete Door",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await unitModel.delete(unitId);
          message.success("Door deleted successfully!");
          try {
            trackEvent("unit_delete_success", { unitId });
          } catch (err) {
            console.warn("[Units] Failed to track unit_delete_success:", err);
          }

          // Invalidate and refetch units data
          await queryClient.invalidateQueries({
            queryKey: propertyKeys.units(user?.uid),
          });
        } catch (error) {
          console.error("Error deleting unit:", error);
          message.error("Failed to delete door. Please try again.");
          try {
            trackEvent("unit_delete_error", { unitId, message: String(error) });
          } catch (err) {
            console.warn("[Units] Failed to track unit_delete_error:", err);
          }
        }
      },
    });
  };

  const normalizeStatusKey = (status) =>
    String(status ?? "")
      .trim()
      .toLowerCase();

  const getStatusColor = (status) => {
    switch (normalizeStatusKey(status)) {
      case "active":
        return "green";
      case "occupied":
        return "blue"; // Will use white text via custom style
      case "vacant":
        return "orange"; // Will use white text via custom style
      case "maintenance":
        return "red";
      default:
        return "default";
    }
  };

  const getUnitPhotoUrl = (unit) => {
    if (Array.isArray(unit?.photos) && unit.photos[0]) return unit.photos[0];
    return null;
  };

  const handleUnitPhotoUpload = async ({ file, onSuccess, onError }) => {
    const uploadFile = file?.originFileObj || file;
    const isImage = (uploadFile.type || file?.type || "").startsWith("image/");
    if (!isImage) {
      message.error("Please upload an image file (JPG, PNG, or WebP).");
      onError?.(new Error("Invalid file type"));
      return;
    }
    if ((uploadFile.size || file?.size || 0) > 5 * 1024 * 1024) {
      message.error("Image must be 5MB or smaller.");
      onError?.(new Error("File too large"));
      return;
    }
    try {
      setPhotoUploading(true);
      const folderId = editingUnit?.id || user?.uid || "pending";
      const imageUrl = await uploadImage(`unit-photos/${folderId}`, uploadFile);
      setUnitPhotoUrl(imageUrl);
      setUnitPhotoFileList([
        {
          uid: file.uid || String(Date.now()),
          name: file.name || uploadFile.name || "door-photo",
          status: "done",
          url: imageUrl,
        },
      ]);
      onSuccess?.(imageUrl, file);
      message.success("Door photo uploaded");
    } catch (err) {
      console.error("[Units] Photo upload failed:", err);
      message.error(err?.message || "Failed to upload photo");
      onError?.(err);
    } finally {
      setPhotoUploading(false);
    }
  };

  const renderUnitPhoto = (unit, { size = 44, mobile = false } = {}) => {
    const photoUrl = getUnitPhotoUrl(unit);
    if (photoUrl) {
      return (
        <UnitPhotoThumb $size={size} $mobile={mobile} aria-hidden>
          <img src={photoUrl} alt="" />
        </UnitPhotoThumb>
      );
    }
    return (
      <UnitPhotoPlaceholder $size={size} $mobile={mobile} aria-hidden>
        <HomeOutlined />
      </UnitPhotoPlaceholder>
    );
  };

  // Helper function to get tenants for a unit
  const getTenantsForUnit = (unitId) => {
    return tenants.filter((tenant) => tenant.unitId === unitId);
  };

  // Helper function to render tenant list
  const renderTenantList = (unitId, isMobile = false) => {
    const unitTenants = getTenantsForUnit(unitId);

    if (unitTenants.length === 0) {
      return (
        <div
          style={{
            fontSize: "0.85rem",
            color: "var(--color-text-light)",
            fontStyle: "italic",
            textAlign: isMobile ? "right" : "left",
          }}
        >
          No tenants assigned
        </div>
      );
    }

    if (unitTenants.length === 1) {
      const tenant = unitTenants[0];
      if (isMobile) {
        // Mobile: right-aligned, simpler layout
        return (
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: "0.9rem", fontWeight: 500 }}>
              {tenant.name}
            </div>
            <div
              style={{ fontSize: "0.8rem", color: "var(--color-text-light)" }}
            >
              ${tenant.monthlyRent?.toLocaleString() || "0"} / month
            </div>
          </div>
        );
      }
      // Desktop: left-aligned with avatar
      return (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Avatar size="small" icon={<UserOutlined />} />
          <div>
            <div style={{ fontSize: "0.9rem", fontWeight: 500 }}>
              {tenant.name}
            </div>
            <div
              style={{ fontSize: "0.8rem", color: "var(--color-text-light)" }}
            >
              ${tenant.monthlyRent?.toLocaleString() || "0"} / month
            </div>
          </div>
        </div>
      );
    }

    // Multiple tenants
    if (isMobile) {
      // Mobile: right-aligned, simpler layout
      return (
        <div style={{ textAlign: "right" }}>
          <div
            style={{
              fontSize: "0.85rem",
              fontWeight: 500,
              color: "var(--color-primary)",
              marginBottom: "4px",
            }}
          >
            {unitTenants.length} tenants
          </div>
          <div style={{ fontSize: "0.8rem", color: "var(--color-text-light)" }}>
            {unitTenants.map((tenant, index) => (
              <div key={tenant.id} style={{ marginBottom: "2px" }}>
                {tenant.name}: ${tenant.monthlyRent?.toLocaleString() || "0"}
              </div>
            ))}
          </div>
        </div>
      );
    }
    // Desktop: left-aligned with icon
    return (
      <div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            marginBottom: "4px",
          }}
        >
          <TeamOutlined
            style={{ color: "var(--color-primary)", fontSize: "0.9rem" }}
          />
          <span
            style={{
              fontSize: "0.85rem",
              fontWeight: 500,
              color: "var(--color-primary)",
            }}
          >
            {unitTenants.length} tenants
          </span>
        </div>
        <div style={{ fontSize: "0.8rem", color: "var(--color-text-light)" }}>
          {unitTenants.map((tenant, index) => (
            <div key={tenant.id} style={{ marginBottom: "2px" }}>
              {tenant.name}: ${tenant.monthlyRent?.toLocaleString() || "0"}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const allColumns = [
    {
      title: "Door",
      dataIndex: "name",
      key: "name",
      width: 180,
      sorter: (a, b) => {
        const nameA = a.name || "";
        const nameB = b.name || "";
        return nameA.localeCompare(nameB);
      },
      render: (name, record) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            width: "100%",
          }}
        >
          {renderUnitPhoto(record, { size: 44 })}
          <div style={{ minWidth: 0, flex: 1, maxWidth: "calc(100% - 54px)" }}>
            <Tooltip title={name} placement="top">
              <div
                style={{
                  fontWeight: 600,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  cursor: "pointer",
                  maxWidth: "100%",
                }}
              >
                {name}
              </div>
            </Tooltip>
            <div
              style={{
                fontSize: "0.85rem",
                color: "var(--color-text-light)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              Door {record.unitNumber || "N/A"}
            </div>
          </div>
        </div>
      ),
    },

    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      width: 220,
      sorter: (a, b) => {
        const addressA = getUnitDisplayAddress(a) || "";
        const addressB = getUnitDisplayAddress(b) || "";
        return addressA.localeCompare(addressB);
      },
      render: (_, record) => (
        <div
          style={{
            fontSize: "0.85rem",
            color: "var(--color-text-light)",
            whiteSpace: "normal",
            wordWrap: "break-word",
            lineHeight: "1.4",
          }}
        >
          {getUnitDisplayAddress(record)}
        </div>
      ),
    },
    {
      title: "Monthly Rent",
      dataIndex: "monthlyRent",
      key: "monthlyRent",
      sorter: (a, b) => {
        const rentA =
          typeof a.monthlyRent === "number"
            ? a.monthlyRent
            : parseFloat(String(a.monthlyRent || 0).replace(/[$,]/g, "")) || 0;
        const rentB =
          typeof b.monthlyRent === "number"
            ? b.monthlyRent
            : parseFloat(String(b.monthlyRent || 0).replace(/[$,]/g, "")) || 0;
        return rentA - rentB;
      },
      render: (rent) => (
        <div
          style={{
            fontSize: "0.875rem",
            fontWeight: 600,
            color: "var(--color-primary)",
          }}
        >
          ${rent ? rent.toLocaleString() : "0"}
        </div>
      ),
    },
    {
      title: "Tenants",
      key: "tenants",
      render: (_, record) => renderTenantList(record.id),
    },
    {
      title: "Bedrooms",
      dataIndex: "bedrooms",
      key: "bedrooms",
      sorter: (a, b) => (a.bedrooms || 0) - (b.bedrooms || 0),
      render: (bedrooms) => (
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              fontSize: "0.875rem",
              fontWeight: 600,
              color: "var(--color-primary)",
            }}
          >
            {bedrooms} bed{bedrooms !== 1 ? "s" : ""}
          </div>
        </div>
      ),
    },
    {
      title: "Bathrooms",
      dataIndex: "bathrooms",
      key: "bathrooms",
      sorter: (a, b) => (a.bathrooms || 0) - (b.bathrooms || 0),
      render: (bathrooms) => (
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              fontSize: "0.875rem",
              fontWeight: 600,
              color: "var(--color-primary)",
            }}
          >
            {bathrooms} bath{bathrooms !== 1 ? "s" : ""}
          </div>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sorter: (a, b) => {
        const statusA = a.status || "";
        const statusB = b.status || "";
        return statusA.localeCompare(statusB);
      },
      render: (status) => {
        const color = getStatusColor(status);
        const key = normalizeStatusKey(status);
        const isOccupied = key === "occupied";
        const isVacant = key === "vacant";
        return (
          <Tag
            color={color}
            style={{
              fontFamily: "var(--font-secondary)",
              ...(isOccupied ? { color: "white", borderColor: "#1890ff" } : {}),
              ...(isVacant ? { color: "white" } : {}),
            }}
          >
            {status}
          </Tag>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Button.Group size="small">
          <Tooltip title="Edit Door">
            <Button
              icon={<EditOutlined />}
              onClick={() => showModal(record)}
              style={{
                border: "none !important",
                background: "none !important",
                boxShadow: "none",
              }}
            />
          </Tooltip>
          <Tooltip title="Delete Door">
            <Button
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteUnit(record.id)}
              style={{
                border: "none",
                background: "none !important",
                boxShadow: "none",
              }}
            />
          </Tooltip>
        </Button.Group>
      ),
    },
  ];

  const OPSCHAT_HIDDEN_COLUMNS = new Set(["monthlyRent"]);
  const columns = isOpsChatOnlyUser
    ? allColumns.filter((c) => !OPSCHAT_HIDDEN_COLUMNS.has(c.key))
    : allColumns;

  const totalUnits = units.length;
  const occupiedUnits = units.filter((unit) => {
    // Check if any tenant is assigned to this unit
    // For debug mode, also check unit.status === 'Occupied' or unit.tenantId
    if (unit.status === "Occupied" || unit.tenantId) {
      return true;
    }
    // For real data, check tenant lease status
    return tenants.some(
      (tenant) => tenant.unitId === unit.id && isTenantActive(tenant)
    );
  }).length;
  const occupancyRate =
    totalUnits > 0 ? ((occupiedUnits / totalUnits) * 100).toFixed(1) : 0;
  // Helper function to safely convert rent to number (handles strings, formatted numbers, etc.)
  const parseRent = (rentValue) => {
    if (typeof rentValue === "number") return rentValue;
    if (typeof rentValue === "string") {
      // Remove $ and commas, then parse
      const cleaned = rentValue.replace(/[$,]/g, "");
      const parsed = parseFloat(cleaned);
      return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  };

  const totalMonthlyRent = units.reduce((sum, u) => {
    const rent = parseRent(u.monthlyRent);
    return sum + rent;
  }, 0);

  // Calculate actual tenant rent by summing unique units that have tenants
  const unitsWithTenants = units.filter(
    (u) => getTenantsForUnit(u.id).length > 0
  );
  const totalTenantRent = unitsWithTenants.reduce((sum, u) => {
    const rent = parseRent(u.monthlyRent);
    return sum + rent;
  }, 0);
  const unitsWithTenantsCount = unitsWithTenants.length;

  const pageSubtitle = isOpsChatOnlyUser
    ? "Add doors to generate a private tenant chat link for each one."
    : "Manage individual doors and track performance";

  if (loading) {
    return (
      <Container className="units-container">
        <PageHeader title="Doors" subtitle={pageSubtitle} icon={HomeOutlined} />
        <StatsSection>
          <Row gutter={[24, 24]} style={{ marginBottom: "2rem" }}>
            {[1, 2, 3].map((i) => (
              <Col xs={24} sm={12} lg={8} key={i}>
                <Card>
                  <Skeleton active paragraph={{ rows: 2 }} />
                </Card>
              </Col>
            ))}
          </Row>
        </StatsSection>
        <Card style={{ marginTop: "2rem" }}>
          <Skeleton active paragraph={{ rows: 8 }} />
        </Card>
      </Container>
    );
  }

  // Visual capacity indicator; turns warning/danger colors as the user
  // approaches their plan cap. Infinite cap (grandfathered / enterprise) shows
  // a friendly "unlimited" pill instead of N/Infinity.
  const isUnlimitedCap = !Number.isFinite(unitCap);
  const unitsRemaining = isUnlimitedCap
    ? Infinity
    : Math.max(0, unitCap - units.length);
  const capacityColor = isUnlimitedCap
    ? "var(--color-primary)"
    : unitsRemaining === 0
    ? "#dc2626" // red-600
    : unitsRemaining <= 3
    ? "#d97706" // amber-600
    : "var(--color-primary)";
  const capacityBg = isUnlimitedCap
    ? "rgba(44, 95, 45, 0.08)"
    : unitsRemaining === 0
    ? "rgba(220, 38, 38, 0.08)"
    : unitsRemaining <= 3
    ? "rgba(217, 119, 6, 0.10)"
    : "rgba(44, 95, 45, 0.08)";

  return (
    <Container className="units-container">
      <PageHeader title="Doors" subtitle={pageSubtitle} icon={HomeOutlined} />

      {/* Summary Statistics: hidden for OpsChat-only users */}
      {!isOpsChatOnlyUser && (
        <StatsSection>
          <Row gutter={[24, 24]} style={{ marginBottom: "2rem" }}>
            <Col xs={24} sm={12} lg={8}>
              <StatCard
                className="stats-card"
                title="Total Doors"
                value={totalUnits}
                prefix={
                  <HomeOutlined style={{ color: "var(--color-primary)" }} />
                }
                valueStyle={{ color: "var(--color-primary)" }}
              />
            </Col>

            <Col xs={24} sm={12} lg={8}>
              <StatCard
                className="stats-card"
                title="Occupancy Rate"
                value={occupancyRate}
                suffix="%"
                prefix={
                  <UserOutlined style={{ color: "var(--color-accent)" }} />
                }
                valueStyle={{ color: "var(--color-primary)" }}
              />
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <StatCard
                className="stats-card"
                title="Monthly Rent Potential"
                value={totalMonthlyRent}
                prefix={
                  <DollarOutlined style={{ color: "var(--color-primary)" }} />
                }
                valueStyle={{ color: "var(--color-primary)" }}
                formatter={(value) => `$${value.toLocaleString()}`}
              />
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <StatCard
                className="stats-card"
                title="Actual Tenant Rent"
                value={totalTenantRent}
                prefix={
                  <UserOutlined style={{ color: "var(--color-accent)" }} />
                }
                valueStyle={{ color: "var(--color-accent)" }}
                formatter={(value) => `$${value.toLocaleString()}`}
              />
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <StatCard
                className="stats-card"
                title="Occupied Doors"
                value={unitsWithTenantsCount}
                prefix={
                  <TeamOutlined style={{ color: "var(--color-primary)" }} />
                }
                valueStyle={{ color: "var(--color-primary)" }}
                suffix={`/ ${totalUnits}`}
              />
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <StatCard
                className="stats-card"
                title="Average Rent per Door"
                value={
                  totalUnits > 0 ? Math.round(totalMonthlyRent / totalUnits) : 0
                }
                prefix={
                  <DollarOutlined style={{ color: "var(--color-accent)" }} />
                }
                valueStyle={{ color: "var(--color-accent)" }}
                formatter={(value) => `$${value.toLocaleString()}`}
              />
            </Col>
          </Row>
        </StatsSection>
      )}

      {/* Doors table */}
      <TableSection>
        <Card
          title={`Individual Doors (${units.length})`}
          style={{ borderRadius: "16px", position: "relative" }}
        >
          {isMobile && (
            <Tooltip title={addDoorCapTooltip}>
              <span>
                <Button
                  type="primary"
                  icon={
                    <PlusOutlined
                      style={{
                        color: "white",
                        filter: "brightness(0) invert(1)",
                      }}
                    />
                  }
                  size="small"
                  onClick={() => {
                    if (!canAddMoreUnits) {
                      setIsUpgradeModalVisible(true);
                      return;
                    }
                    showModal();
                  }}
                  disabled={!canAddMoreUnits}
                  style={{
                    position: "absolute",
                    top: "12px",
                    right: "12px",
                    zIndex: 10,
                    background: "var(--color-primary)",
                    borderColor: "var(--color-primary)",
                    borderRadius: "8px",
                    width: "32px",
                    height: "32px",
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                />
              </span>
            </Tooltip>
          )}
          <Tooltip title={addDoorCapTooltip}>
            <span>
              <AddButton
                type="primary"
                icon={
                  <PlusOutlined
                    style={{
                      fontSize: 14,
                      marginTop: "8px",
                      filter: "brightness(0) invert(1)",
                    }}
                  />
                }
                size="small"
                className="add-table-action"
                onClick={() => {
                  if (!canAddMoreUnits) {
                    setIsUpgradeModalVisible(true);
                    return;
                  }
                  showModal();
                }}
                disabled={!canAddMoreUnits}
                style={{
                  position: "absolute",
                  top: "12px",
                  right: "12px",
                  zIndex: 10,
                  height: "36px",
                  padding: "2px 10px",
                  fontSize: "13px !important",
                  fontFamily: "var(--font-secondary)",
                  fontWeight: 600,
                  letterSpacing: "0.2px",
                  lineHeight: "24px",
                  whiteSpace: "nowrap",
                  display: isMobile ? "none" : "flex",
                }}
              >
                Add Door
              </AddButton>
            </span>
          </Tooltip>

          {isMobile ? (
            units.length === 0 ? (
              <EmptyState
                icon={<HomeOutlined />}
                title={
                  isFreeUser
                    ? "Welcome. Let Leo set up your first property."
                    : isOpsChatOnlyUser
                    ? "Add your first door to get a tenant chat link."
                    : "Your portfolio is empty. Add your first property to start tracking ROI."
                }
                subtitle={
                  isFreeUser
                    ? "Summon Leo to walk through the first door in chat. Or add one manually."
                    : isOpsChatOnlyUser
                    ? "Each door gets its own private chat link. No app install needed for your tenant."
                    : "Add a door to see rent, occupancy, and expenses in one place."
                }
                actions={[
                  ...(isFreeUser
                    ? [
                        <EmptyActionButton
                          key="summon-leo"
                          type="primary"
                          icon={
                            <ThunderboltOutlined
                              style={{ filter: "brightness(0) invert(1)" }}
                            />
                          }
                          onClick={handleSummonLeo}
                        >
                          Summon Leo
                        </EmptyActionButton>,
                      ]
                    : []),
                  <EmptyActionButton
                    key="add-unit"
                    type={isFreeUser ? "default" : "primary"}
                    icon={
                      <PlusOutlined
                        style={
                          isFreeUser
                            ? undefined
                            : { filter: "brightness(0) invert(1)" }
                        }
                      />
                    }
                    onClick={() => {
                      if (!canAddMoreUnits) {
                        setIsUpgradeModalVisible(true);
                        return;
                      }
                      showModal();
                      trackEvent("unit_create_open", { source: "empty_state" });
                    }}
                    disabled={!canAddMoreUnits}
                  >
                    Add Door
                  </EmptyActionButton>,
                ]}
              />
            ) : (
              <MobileUnitsCards>
                {units.map((unit) => (
                  <UnitCard key={unit.id}>
                    <MobileCardHeader>
                      {renderUnitPhoto(unit, { size: 56, mobile: true })}
                      <MobileCardTitle>
                        <MobileUnitName>{unit.name}</MobileUnitName>
                        <MobileUnitNumber>
                          Door {unit.unitNumber || "N/A"}
                        </MobileUnitNumber>
                      </MobileCardTitle>
                    </MobileCardHeader>
                    <MobileCardContent>
                      <MobileDetailsSection>
                        <MobileDetailRow>
                          <MobileDetailLabel>Address</MobileDetailLabel>
                          <MobileDetailValue>
                            {getUnitDisplayAddress(unit)}
                          </MobileDetailValue>
                        </MobileDetailRow>
                        {!isOpsChatOnlyUser && (
                          <MobileDetailRow>
                            <MobileDetailLabel>Monthly Rent</MobileDetailLabel>
                            <MobileDetailValue>
                              ${unit.monthlyRent?.toLocaleString() || "0"}
                            </MobileDetailValue>
                          </MobileDetailRow>
                        )}
                        <MobileDetailRow>
                          <MobileDetailLabel>Bedrooms</MobileDetailLabel>
                          <MobileDetailValue>
                            {unit.bedrooms} bed{unit.bedrooms !== 1 ? "s" : ""}
                          </MobileDetailValue>
                        </MobileDetailRow>
                        <MobileDetailRow>
                          <MobileDetailLabel>Bathrooms</MobileDetailLabel>
                          <MobileDetailValue>
                            {unit.bathrooms} bath
                            {unit.bathrooms !== 1 ? "s" : ""}
                          </MobileDetailValue>
                        </MobileDetailRow>
                        <MobileDetailRow>
                          <MobileDetailLabel>Status</MobileDetailLabel>
                          <MobileStatusTag
                            color={getStatusColor(unit.status)}
                            $isOccupied={
                              normalizeStatusKey(unit.status) === "occupied"
                            }
                            $isVacant={
                              normalizeStatusKey(unit.status) === "vacant"
                            }
                          >
                            {unit.status}
                          </MobileStatusTag>
                        </MobileDetailRow>
                        <MobileDetailRow>
                          <MobileDetailLabel>Tenants</MobileDetailLabel>
                          <MobileDetailValue>
                            {renderTenantList(unit.id, true)}
                          </MobileDetailValue>
                        </MobileDetailRow>
                      </MobileDetailsSection>
                      <MobileActionButtons>
                        <MobileActionButton
                          type="default"
                          icon={
                            <EditOutlined
                              style={{ filter: "brightness(0) invert(1)" }}
                            />
                          }
                          onClick={() => showModal(unit)}
                        />
                        <MobileActionButton
                          type="default"
                          icon={
                            <DeleteOutlined
                              style={{ filter: "brightness(0) invert(1)" }}
                            />
                          }
                          onClick={() => handleDeleteUnit(unit.id)}
                          danger
                        />
                      </MobileActionButtons>
                    </MobileCardContent>
                  </UnitCard>
                ))}
              </MobileUnitsCards>
            )
          ) : (
            <Table
              columns={columns}
              dataSource={units}
              rowKey="id"
              scroll={{ x: "max-content" }}
              pagination={{
                pageSize: pageSize,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} doors`,
                onShowSizeChange: handlePageSizeChange,
                pageSizeOptions: ["10", "20", "50", "100"],
              }}
              loading={loading}
              locale={{
                emptyText: (
                  <EmptyState
                    icon={<HomeOutlined />}
                    title={
                      isFreeUser
                        ? "Welcome. Let Leo set up your first property."
                        : isOpsChatOnlyUser
                        ? "Add your first door to get a tenant chat link."
                        : "Your portfolio is empty. Add your first property to start tracking ROI."
                    }
                    subtitle={
                      isFreeUser
                        ? "Summon Leo to walk through the first door in chat. Or add one manually."
                        : isOpsChatOnlyUser
                        ? "Each door gets its own private chat link. No app install needed for your tenant."
                        : "Add a door to see rent, occupancy, and expenses in one place."
                    }
                    actions={[
                      ...(isFreeUser
                        ? [
                            <EmptyActionButton
                              key="summon-leo"
                              type="primary"
                              icon={
                                <ThunderboltOutlined
                                  style={{ filter: "brightness(0) invert(1)" }}
                                />
                              }
                              onClick={handleSummonLeo}
                            >
                              Summon Leo
                            </EmptyActionButton>,
                          ]
                        : []),
                      <EmptyActionButton
                        key="add-unit"
                        type={isFreeUser ? "default" : "primary"}
                        icon={
                          <PlusOutlined
                            style={
                              isFreeUser
                                ? undefined
                                : { filter: "brightness(0) invert(1)" }
                            }
                          />
                        }
                        onClick={() => {
                          if (!canAddMoreUnits) {
                            setIsUpgradeModalVisible(true);
                            return;
                          }
                          showModal();
                          trackEvent("unit_create_open", {
                            source: "empty_state",
                          });
                        }}
                        disabled={!canAddMoreUnits}
                      >
                        Add Door
                      </EmptyActionButton>,
                    ]}
                  />
                ),
              }}
            />
          )}
        </Card>
      </TableSection>

      {/* Add/Edit Unit Modal */}
      <StyledModal
        title={editingUnit ? "Edit Door" : "Add New Door"}
        open={isModalVisible}
        centered
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        confirmLoading={modalLoading}
        width={600}
        okText={editingUnit ? "Update" : "Add"}
        cancelText="Cancel"
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            status: "Active",
          }}
        >
          <Form.Item
            name="name"
            label="Door Name/Description"
            rules={[{ required: true, message: "Enter door name" }]}
          >
            <Input placeholder="e.g., Door A, 2BR Apartment, Office Suite" />
          </Form.Item>

          <DoorPhotoSection>
            <DoorPhotoLabel>Door photo</DoorPhotoLabel>
            <DoorPhotoHint>
              Optional. Shows on your Doors list. Listings can reuse this photo.
            </DoorPhotoHint>
            <Upload.Dragger
              accept="image/*"
              listType="picture"
              maxCount={1}
              fileList={unitPhotoFileList}
              disabled={photoUploading || modalLoading}
              customRequest={handleUnitPhotoUpload}
              onRemove={() => {
                setUnitPhotoUrl(null);
                setUnitPhotoFileList([]);
                return true;
              }}
              beforeUpload={(file) => {
                const isImage = (file.type || "").startsWith("image/");
                if (!isImage) {
                  message.error("Please upload an image file.");
                  return Upload.LIST_IGNORE;
                }
                if (file.size > 5 * 1024 * 1024) {
                  message.error("Image must be 5MB or smaller.");
                  return Upload.LIST_IGNORE;
                }
                return true;
              }}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                {photoUploading
                  ? "Uploading…"
                  : "Click or drag a door photo"}
              </p>
              <p className="ant-upload-hint">JPG, PNG, or WebP · max 5MB</p>
            </Upload.Dragger>
          </DoorPhotoSection>

          <div
            style={{
              fontSize: 14,
              color: "rgba(0,0,0,0.88)",
              marginBottom: 8,
              marginTop: 16,
            }}
          >
            Door Address
          </div>
          <Form.Item
            name="address_street"
            label="Street"
            rules={[{ required: true, message: "Enter street address" }]}
          >
            <Input placeholder="e.g., 123 Main St" />
          </Form.Item>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="address_city"
                label="City"
                rules={[{ required: true, message: "Enter city" }]}
              >
                <Input placeholder="e.g., St. Louis" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item
                name="address_state"
                label="State"
                rules={[
                  { required: true, message: "Enter state (2 letters)" },
                  { len: 2, message: "State must be 2-letter code (e.g., MO)" },
                ]}
              >
                <Input
                  placeholder="MO"
                  maxLength={2}
                  style={{ textTransform: "uppercase" }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item
                name="address_zip"
                label="Zip"
                rules={[
                  { required: true, message: "Enter ZIP code" },
                  {
                    pattern: /^\d{5}$/,
                    message: "ZIP must be 5 digits",
                  },
                ]}
              >
                <Input
                  placeholder="63101"
                  maxLength={5}
                  style={{ width: "100%" }}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, "").slice(0, 5);
                    form.setFieldValue("address_zip", v);
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            noStyle
            shouldUpdate={(prev, cur) =>
              prev.address_street !== cur.address_street ||
              prev.address_city !== cur.address_city ||
              prev.address_state !== cur.address_state ||
              prev.address_zip !== cur.address_zip
            }
          >
            {({ getFieldValue }) => {
              const street = (getFieldValue("address_street") || "").trim();
              const city = (getFieldValue("address_city") || "").trim();
              const state = (getFieldValue("address_state") || "")
                .trim()
                .toUpperCase();
              const zip = (getFieldValue("address_zip") || "").trim();
              const full = [street, city, state, zip]
                .filter(Boolean)
                .join(", ");
              return (
                <div
                  style={{
                    fontSize: "0.85rem",
                    color: "var(--color-text-light)",
                    marginTop: -8,
                    marginBottom: 16,
                  }}
                >
                  {full
                    ? `Full address: ${full}`
                    : "Full address preview will appear here"}
                </div>
              );
            }}
          </Form.Item>

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={24} md={isOpsChatOnlyUser ? 24 : 12}>
              <Form.Item
                name="unitNumber"
                label="Door Number/Identifier"
                rules={[{ required: true, message: "Enter door number" }]}
              >
                <Input placeholder="e.g., A101, 1A, Studio 1" />
              </Form.Item>
            </Col>
            {!isOpsChatOnlyUser && (
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="monthlyRent"
                  label="Monthly Rent"
                  rules={[{ required: true, message: "Enter monthly rent" }]}
                  className="monthly-rent-field"
                >
                  <Input type="number" prefix="$" placeholder="0" min={0} />
                </Form.Item>
              </Col>
            )}
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={24} md={12}>
              <Form.Item
                name="bedrooms"
                label="Bedrooms"
                rules={[
                  { required: true, message: "Enter number of bedrooms" },
                ]}
              >
                <InputNumber
                  min={0}
                  max={10}
                  style={{ width: "100%" }}
                  placeholder="0"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12}>
              <Form.Item
                name="bathrooms"
                label="Bathrooms"
                rules={[
                  { required: true, message: "Enter number of bathrooms" },
                ]}
              >
                <InputNumber
                  min={0}
                  max={10}
                  step={0.5}
                  style={{ width: "100%" }}
                  placeholder="0"
                />
              </Form.Item>
            </Col>
          </Row>

          {!isOpsChatOnlyUser && (
            <Form.Item
              name={["mortgage", "monthlyPayment"]}
              label="Monthly mortgage payment ($)"
              tooltip="Used for portfolio DSCR on the dashboard. Leave 0 if no debt on this door."
            >
              <InputNumber
                min={0}
                step={100}
                placeholder="0"
                style={{ width: "100%" }}
                formatter={(value) =>
                  value != null
                    ? `${Number(value).toLocaleString("en-US")}`
                    : ""
                }
                parser={(displayValue) =>
                  String(displayValue || "").replace(/,/g, "") || "0"
                }
              />
            </Form.Item>
          )}

          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: "Please select status" }]}
          >
            <Select>
              <Option value="Active">Active</Option>
              <Option value="Vacant">Vacant</Option>
              <Option value="Maintenance">Maintenance</Option>
              <Option value="Development">Development</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="notes"
            label={
              isMobile ? (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 4 }}
                >
                  <span>Additional Notes</span>
                  <span
                    style={{
                      fontSize: "12px",
                      color: "var(--color-text-light)",
                      fontWeight: 400,
                    }}
                  >
                    Leo AI can share this with tenants in tenant chat.
                    Don&apos;t enter anything you don&apos;t want them to know.
                  </span>
                </div>
              ) : (
                "Additional Notes"
              )
            }
            tooltip={
              isMobile
                ? undefined
                : "Leo AI can share this with tenants in tenant chat. Don't enter anything you don't want them to know."
            }
          >
            <TextArea
              rows={3}
              placeholder="Any additional information about the property..."
            />
          </Form.Item>

          {!editingUnit && !isOpsChatOnlyUser && (
            <div
              style={{
                marginTop: "24px",
                marginBottom: "16px",
                padding: "16px",
                backgroundColor: "#f0fdf4",
                borderRadius: "8px",
                border: "1px solid #bbf7d0",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "12px",
                }}
              >
                <DollarOutlined
                  style={{
                    color: "var(--color-primary)",
                    fontSize: "18px",
                    marginTop: "2px",
                    flexShrink: 0,
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      color: "#1e293b",
                      marginBottom: "4px",
                      fontSize: "14px",
                    }}
                  >
                    Automatic Payment Creation
                  </div>
                  <div
                    style={{
                      color: "#475569",
                      fontSize: "13px",
                      lineHeight: "1.5",
                    }}
                  >
                    Rent payment records are automatically created by the system
                    within 24 hours when a tenant assigned to this door has a
                    monthly rent amount and rent due day set. These are
                    placeholders for upcoming rent payments that can be viewed
                    and tracked in the "Rent" tab.
                  </div>
                </div>
              </div>
            </div>
          )}
        </Form>
      </StyledModal>

      {/* Upgrade Modal */}
      <UpgradeModal
        visible={isUpgradeModalVisible}
        onCancel={() => setIsUpgradeModalVisible(false)}
        currentUnits={units.length}
        currentTier={normalizedTier}
        pricingTierGroup={pricingTierGroup}
      />
    </Container>
  );
};

export default Units;

// Styled Components
const Container = styled.div`
  min-height: 100vh;
  background: var(--color-bg-primary);
  padding: 2rem;
  width: ${LAYOUT_CONSTANTS.DESKTOP_CONTENT_WIDTH};
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 1rem;
    width: 95vw;
    min-width: ${LAYOUT_CONSTANTS.MIN_CONTENT_WIDTH};
  }

  @media (min-width: 768px) and (max-width: 1200px) {
    padding: 1.5rem;
    width: 90vw;
  }

  /* Apply the new container class for better background */
  &.units-container {
    background: var(--color-bg-primary) !important;
  }

  .ant-select-arrow {
    position: absolute !important;
    right: 11px !important;
    top: 50% !important;
    transform: translateY(-50%) !important;
    margin: 0 !important;
    filter: brightness(1) invert(0) !important;

    &:hover {
      filter: brightness(1) invert(0) !important;
    }
  }

  /* Uniform border colors for all input types when unfocused */
  /* Base inputs (like Unit Number/Identifier) */
  .ant-input:not(.ant-input-number-input) {
    border: 2px solid #cbd5e0 !important;
    border-radius: 8px !important;
    transition: all 0.2s ease !important;

    &:hover {
      border-color: #40a9ff !important;
    }

    &:focus {
      border-color: var(--color-primary) !important;
      box-shadow: 0 0 0 3px rgba(44, 95, 45, 0.1) !important;
    }
  }

  /* TextArea */
  textarea.ant-input {
    border: 2px solid #cbd5e0 !important;

    &:hover {
      border-color: #40a9ff !important;
    }

    &:focus {
      border-color: var(--color-primary) !important;
      box-shadow: 0 0 0 3px rgba(44, 95, 45, 0.1) !important;
    }
  }

  /* InputNumber without prefix (bedrooms, bathrooms) - apply border to wrapper */
  /* Use higher specificity to override Ant Design defaults */
  .ant-form-item .ant-input-number:not(.ant-input-number-disabled),
  .ant-input-number:not(.ant-input-number-disabled) {
    border: 2px solid #cbd5e0 !important;
    border-radius: 8px !important;
    transition: all 0.2s ease !important;

    .ant-input-number-input {
      border: none !important;
      box-shadow: none !important;
      outline: none !important;
    }

    &:hover:not(.ant-input-number-focused) {
      border-color: #40a9ff !important;
    }

    &:focus,
    &.ant-input-number-focused {
      border-color: var(--color-primary) !important;
      box-shadow: 0 0 0 3px rgba(44, 95, 45, 0.1) !important;
    }
  }

  /* InputNumber with addonBefore (Monthly Rent) - group wrapper styling */
  .ant-form-item .ant-input-number-group-wrapper,
  .ant-input-number-group-wrapper {
    .ant-input-number-group-addon {
      border: 2px solid #cbd5e0 !important;
      border-right: none !important;
      border-radius: 8px 0 0 8px !important;
      background: #fff;
      padding: 0 11px;
      color: var(--color-text);
    }

    .ant-input-number {
      border: 2px solid #cbd5e0 !important;
      border-left: none !important;
      border-radius: 0 8px 8px 0 !important;

      .ant-input-number-input {
        border: none !important;
      }
    }

    &:hover .ant-input-number-group-addon,
    &:hover .ant-input-number {
      border-color: #40a9ff !important;
    }

    &.ant-input-number-group-wrapper-focused .ant-input-number-group-addon,
    &.ant-input-number-group-wrapper-focused .ant-input-number,
    .ant-input-number-focused {
      border-color: var(--color-primary) !important;
      box-shadow: 0 0 0 3px rgba(44, 95, 45, 0.1) !important;
    }
  }

  /* InputNumber with prefix (Monthly Rent) - match other input fields - ULTRA HIGH SPECIFICITY */
  &.units-container .ant-form-item .ant-input-affix-wrapper,
  .ant-form-item .ant-input-affix-wrapper,
  .ant-input-affix-wrapper {
    border: 2px solid #cbd5e0 !important;
    border-radius: 8px !important;
    transition: all 0.2s ease !important;

    /* Remove inner separator border completely */
    .ant-input-prefix {
      border-right: none !important;
      border: none !important;
      border-color: transparent !important;
      margin-right: 8px !important;
      padding-right: 0 !important;
      background: transparent !important;
    }

    /* Remove separator pseudo-elements */
    .ant-input-prefix::after,
    .ant-input-prefix::before {
      display: none !important;
      content: none !important;
      border: none !important;
    }

    /* Remove any borders from inner elements */
    .ant-input-number {
      border: none !important;

      .ant-input-number-input {
        border: none !important;
        box-shadow: none !important;
        outline: none !important;
      }
    }

    &:hover:not(.ant-input-affix-wrapper-focused) {
      border-color: #40a9ff !important;
    }

    &:focus,
    &.ant-input-affix-wrapper-focused {
      border-color: var(--color-primary) !important;
      box-shadow: 0 0 0 3px rgba(44, 95, 45, 0.1) !important;
    }

    .ant-input {
      border: none !important;
      box-shadow: none !important;
      outline: none !important;
    }
  }

  /* Select dropdown */
  .ant-form-item .ant-select-selector,
  .ant-select-selector {
    border: 2px solid #cbd5e0 !important;
    border-radius: 8px !important;
    transition: all 0.2s ease !important;

    &:hover {
      border-color: #40a9ff !important;
    }
  }
`;

const CapacityIndicator = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 1rem;
  margin: 0 0 1.25rem 0;
  border-radius: 999px;
  border: 1.5px solid;
  font-family: var(--font-secondary);
  font-size: 0.9rem;
  font-weight: 600;
  max-width: 100%;
  flex-wrap: wrap;

  .label {
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-size: 0.75rem;
    opacity: 0.85;
  }

  .count {
    font-family: var(--font-primary);
    font-size: 1.05rem;
    font-weight: 800;
    letter-spacing: -0.01em;
  }

  .hint {
    font-size: 0.8rem;
    font-weight: 500;
    opacity: 0.95;
  }

  @media (max-width: 480px) {
    gap: 0.5rem;
    padding: 0.45rem 0.8rem;
    font-size: 0.85rem;

    .count {
      font-size: 0.95rem;
    }

    .hint {
      flex-basis: 100%;
    }
  }
`;

const AddButton = styled(Button)`
  background: var(--color-primary) !important;
  border: 2px solid var(--color-primary) !important;
  color: white !important;
  border-radius: 16px;
  height: 48px;
  font-size: 1rem !important;
  font-weight: 700;
  font-family: var(--font-secondary);
  box-shadow: 0 6px 24px rgba(44, 95, 45, 0.2);
  padding: 0 2rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  z-index: 1;

  .ant-btn-icon {
    filter: brightness(0) invert(1) !important;
  }

  &:hover:not(:disabled) {
    background: var(--color-primary-dark) !important;
    border-color: var(--color-primary-dark) !important;
    transform: translateY(-3px);
    box-shadow: 0 12px 40px rgba(44, 95, 45, 0.3);
  }

  &:active {
    transform: translateY(-1px);
  }
`;

const StatsSection = styled.div`
  margin-bottom: 2rem;
  width: 100%;

  /* Standardize statistic typography for this section */
  .ant-card {
    --stat-title-size: 0.875rem;
    --stat-value-size: 1.5rem;
    --stat-suffix-size: 1.5rem;
    --stat-icon-size: 1.5rem;
  }

  .ant-statistic {
    text-align: center;
  }

  .ant-statistic-title {
    font-size: var(--stat-title-size) !important;
    line-height: 1.25;
    font-weight: 600;
    color: var(--color-text-light);
    margin-bottom: 0.5rem;
  }

  .ant-statistic-content {
    font-size: var(--stat-value-size) !important;
    line-height: 1.1;
    font-weight: 700;
    color: var(--color-text) !important;
  }

  .ant-statistic-content-value,
  .ant-statistic-content-value-int,
  .ant-statistic-content-value-decimal {
    font-size: var(--stat-value-size) !important;
    line-height: 1.1;
    font-weight: 700;
    color: var(--color-text) !important;
  }

  .ant-statistic-content-suffix,
  .ant-statistic-content-prefix {
    font-size: var(--stat-suffix-size) !important;
    font-weight: 700;
    line-height: 1.2;
  }

  /* Ensure prefix icons have consistent visual weight */
  .ant-statistic .ant-statistic-content-prefix svg,
  .ant-statistic .ant-statistic-content-prefix .anticon {
    font-size: var(--stat-icon-size) !important;
    width: var(--stat-icon-size) !important;
    height: var(--stat-icon-size) !important;
  }

  /* Responsive adjustments */
  @media (max-width: 768px) {
    .ant-card {
      --stat-title-size: 0.8rem;
      --stat-value-size: 1.25rem;
      --stat-suffix-size: 1.25rem;
      --stat-icon-size: 1.25rem;
    }
  }

  /* Ensure our shared StatCard renders with these sizes */
  .stats-card {
    .ant-statistic-title {
      font-size: var(--stat-title-size) !important;
    }
    .ant-statistic-content {
      font-size: var(--stat-value-size) !important;
    }
    .ant-statistic-content-value,
    .ant-statistic-content-value-int,
    .ant-statistic-content-value-decimal {
      font-size: var(--stat-value-size) !important;
    }
    .ant-statistic-content-suffix,
    .ant-statistic-content-prefix {
      font-size: var(--stat-suffix-size) !important;
    }
    .ant-statistic-content-prefix svg,
    .ant-statistic-content-prefix .anticon {
      font-size: var(--stat-icon-size) !important;
      width: var(--stat-icon-size) !important;
      height: var(--stat-icon-size) !important;
    }
  }
`;

const TableSection = styled.div`
  margin-bottom: 2rem;
  width: 100%;

  .ant-card {
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 6px 24px rgba(44, 95, 45, 0.08);
    border: 2px solid var(--color-border-light);
    width: 100%;
  }

  /* Let the desktop table use the card's full horizontal width. */
  @media (min-width: 769px) {
    .ant-card-body {
      padding-top: 0 !important;
      padding-left: 0 !important;
      padding-right: 0 !important;
    }
  }

  .ant-table {
    min-width: unset !important;
  }

  .ant-table-tbody > tr > td {
    white-space: nowrap;
  }

  .ant-table-tbody > tr > td:first-child {
    white-space: normal;
    max-width: 200px;
  }

  .ant-table-tbody > tr > td:nth-child(2) {
    white-space: normal;
    word-wrap: break-word;
    max-width: 250px;
  }

  .ant-card-head {
    background: var(--color-primary) !important;
    border-bottom: 2px solid var(--color-border-light);
    padding: 0;
  }

  .ant-card-head-title {
    color: white !important;
    font-family: var(--font-primary);
    font-weight: 600;
    font-size: 1.25rem;
    padding: 1.5rem 2rem 1rem 2rem;
  }

  .ant-table {
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 6px 24px rgba(44, 95, 45, 0.08);
    border: 2px solid var(--color-border-light);
    width: 100%;
    table-layout: fixed; /* Use fixed layout for better column control */
  }

  .ant-table-thead > tr > th {
    background: var(--color-bg-tertiary);
    border-bottom: 2px solid var(--color-border-light);
    font-family: var(--font-secondary);
    font-weight: 600;
    color: #ffffff;
    padding: 1.25rem 1rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .ant-table-tbody > tr > td {
    padding: 1.25rem 1rem;
    border-bottom: 1px solid var(--color-border-light);
    font-family: var(--font-secondary);
    overflow: hidden;
    text-overflow: ellipsis;
    word-wrap: break-word;
    vertical-align: middle;
  }

  .ant-table-tbody > tr:hover > td {
    background: var(--color-primary-glow);
  }

  .ant-table-tbody > tr:last-child > td {
    border-bottom: none;
  }

  .ant-tag {
    border-radius: 8px;
    font-weight: 600;
    font-size: 0.875rem;
    padding: 0.25rem 0.75rem;
    border: none;
  }

  .ant-btn-text {
    border-radius: 8px;
    transition: all 0.2s ease;

    &:hover {
      background: var(--color-primary-glow);
      color: var(--color-primary);
    }
  }

  /* Fix pagination alignment */
  .ant-pagination {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .ant-pagination-total-text {
    display: flex;
    align-items: center;
    height: 32px;
    line-height: 32px;
  }

  .ant-pagination-item,
  .ant-pagination-prev,
  .ant-pagination-next {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 32px;
    min-width: 32px;
    line-height: 32px;
  }

  .ant-pagination-options {
    display: flex;
    align-items: center;
    height: 32px;
    margin-left: 0;
    position: relative;
    z-index: 1;

    .ant-select {
      display: flex;
      align-items: center;
      height: 32px;
      position: relative;
      z-index: 100;
    }

    .ant-select-selector {
      display: flex;
      align-items: center;
      height: 32px !important;
      padding: 0 11px;
    }

    .ant-select-selection-item {
      display: flex;
      align-items: center;
      line-height: 32px;
      height: 32px;
    }

    .ant-select-arrow {
      display: flex;
      align-items: center;
      top: 50%;
      transform: translateY(-50%);
      margin-top: 0 !important;
    }
  }

  /* Prevent table scroll when pagination dropdown is open */
  .ant-table-pagination {
    position: relative;
    z-index: 10;
  }

  /* Prevent table body from scrolling when pagination dropdown opens */
  .ant-table-body {
    scroll-behavior: auto !important;
    overflow-anchor: none !important;
  }

  /* Prevent scroll on table container */
  .ant-table-container {
    scroll-behavior: auto !important;
    overflow-anchor: none !important;
  }

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    .ant-table-thead > tr > th,
    .ant-table-tbody > tr > td {
      padding: 0.75rem 0.75rem;
    }

    .add-table-action {
      position: sticky !important;
      top: 8px !important;
      right: 8px !important;
      align-self: flex-end;
      height: 32px !important;
      padding: 2px 10px !important;
      font-size: 12px !important;
    }
  }
`;

// Mobile Card Components
const MobileUnitsCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0.5rem 0;
`;

const UnitCard = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #f1f5f9 100%);
  border-radius: 16px;
  border: 2px solid #e2e8f0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(
      90deg,
      var(--color-primary) 0%,
      var(--color-primary-dark) 100%
    );
  }
`;

const MobileCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem 1.5rem 1rem;
  border-bottom: 1px solid #e2e8f0;
`;

const UnitPhotoThumb = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "$size" && prop !== "$mobile",
})`
  width: ${(p) => p.$size || 44}px;
  height: ${(p) => p.$size || 44}px;
  border-radius: 10px;
  overflow: hidden;
  flex-shrink: 0;
  background: #eef2f0;
  border: 1px solid #e5e7eb;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const UnitPhotoPlaceholder = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "$size" && prop !== "$mobile",
})`
  width: ${(p) => p.$size || 44}px;
  height: ${(p) => p.$size || 44}px;
  border-radius: 10px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(145deg, #f1f5f9 0%, #e2e8f0 100%);
  border: 1px solid #e2e8f0;
  color: #94a3b8;
  font-size: ${(p) => Math.max(16, Math.round((p.$size || 44) * 0.4))}px;

  .anticon {
    color: #94a3b8 !important;
  }
`;

const DoorPhotoSection = styled.div`
  margin: 4px 0 12px;

  .ant-upload.ant-upload-drag {
    height: auto !important;
    min-height: 0 !important;
    margin: 0 !important;
    padding: 10px 12px !important;
    border: 1.5px dashed #cbd5e1 !important;
    border-radius: 10px !important;
    background: #f8fafc !important;
  }

  .ant-upload.ant-upload-drag:hover {
    border-color: var(--color-primary) !important;
  }

  .ant-upload-drag-container {
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 2px;
    padding: 0 !important;
  }

  .ant-upload-drag-icon {
    margin: 0 !important;
    height: auto !important;
  }

  .ant-upload-drag-icon .anticon {
    color: var(--color-primary) !important;
    font-size: 22px !important;
  }

  .ant-upload-text {
    margin: 0 !important;
    padding: 0 !important;
    font-size: 0.8125rem !important;
    font-weight: 600 !important;
    line-height: 1.3 !important;
  }

  .ant-upload-hint {
    margin: 0 !important;
    padding: 0 !important;
    font-size: 0.75rem !important;
    line-height: 1.3 !important;
  }

  .ant-upload-list {
    margin-top: 8px !important;
  }
`;

const DoorPhotoLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.88);
  margin-bottom: 2px;
`;

const DoorPhotoHint = styled.p`
  margin: 0 0 6px;
  font-size: 0.8125rem;
  color: rgba(0, 0, 0, 0.55);
  line-height: 1.35;
`;

const MobileCardTitle = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const MobileUnitName = styled.div`
  font-weight: 600;
  font-size: 1rem;
  color: var(--color-text);
`;

const MobileUnitNumber = styled.div`
  font-size: 0.85rem;
  color: var(--color-text-light);
`;

const MobileCardContent = styled.div`
  padding: 1rem 1.5rem 1.5rem;
`;

const MobileDetailsSection = styled.div`
  margin-bottom: 1.5rem;
`;

const MobileDetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const MobileDetailLabel = styled.div`
  font-size: 0.85rem;
  color: var(--color-text-light);
  font-weight: 500;
`;

const MobileDetailValue = styled.div`
  font-size: 0.9rem;
  color: var(--color-text);
  font-weight: 600;
  text-align: right;
  flex: 1;
  margin-left: 1rem;
`;

const MobileStatusTag = styled(Tag)`
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.8rem;
  padding: 0.2rem 0.6rem;
  border: none;
  ${(props) =>
    props.$isOccupied
      ? `
    color: #0c4a6e !important;
    background: #e0f2fe !important;
    border: 1px solid #7dd3fc !important;
  `
      : props.$isVacant
      ? `
    color: #7c2d12 !important;
    background: #ffedd5 !important;
    border: 1px solid #fdba74 !important;
  `
      : ""}
`;

const MobileActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  background: transparent;
  width: 100%;
`;

const MobileActionButton = styled(Button)`
  flex: 1;
  height: 40px;
  border-radius: 8px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;

  &.ant-btn-primary {
    background: var(--color-primary);
    border-color: var(--color-primary);

    &:hover {
      background: var(--color-primary-dark);
      border-color: var(--color-primary-dark);
    }

    .anticon {
      color: white !important;
    }
  }

  &.ant-btn-default {
    background: var(--color-primary);
    border-color: var(--color-primary);
    color: white;

    &:hover {
      background: var(--color-primary-dark);
      border-color: var(--color-primary-dark);
      color: white;
    }

    .anticon {
      color: white !important;
    }

    &:hover .anticon {
      color: white !important;
    }
  }

  &.ant-btn-dangerous {
    background: #ff4d4f;
    border-color: #ff4d4f;
    color: white;

    &:hover {
      background: #ff7875;
      border-color: #ff7875;
    }

    .anticon {
      color: white !important;
    }
  }
`;

const EmptyActionButton = styled(Button)`
  &&& {
    margin-top: 1rem !important;
    height: 44px !important;
    font-size: 1rem !important;
    font-weight: 600 !important;
    border-radius: 8px !important;
    padding: 0 2rem !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    background: var(--color-primary) !important;
    border-color: var(--color-primary) !important;
    color: white !important;

    &:hover {
      background: var(--color-primary-dark) !important;
      border-color: var(--color-primary-dark) !important;
      color: white !important;
    }

    .anticon {
      color: white !important;
      filter: brightness(0) invert(1) !important;
    }

    span {
      color: white !important;
    }
  }
`;
